import React from 'react';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { AlertCircle, Briefcase, Loader2, TrendingUp, Users } from 'lucide-react';
import { cn } from '@/lib/utils';
import type { ProjectStatsResponse } from '@/services/projectsApi';
import type { ActiveMetric } from '@/components/Pages/ActiveProjects/useActiveProjects';

interface ProjectStatsCardsProps {
  stats: ProjectStatsResponse;
  isLoadingStats: boolean;
  activeMetric: ActiveMetric | null;
  onMetricClick: (metric: ActiveMetric) => void;
}

export const ProjectStatsCards: React.FC<ProjectStatsCardsProps> = ({
  stats,
  isLoadingStats,
  activeMetric,
  onMetricClick,
}) => {
  const keyboardActivate = (metric: ActiveMetric) => (e: React.KeyboardEvent) => {
    if (e.key === 'Enter' || e.key === ' ') {
      e.preventDefault();
      onMetricClick(metric);
    }
  };

  return (
    <div className="grid gap-4 md:grid-cols-2 lg:grid-cols-4" data-tour="projects-stats">
      {/* Total Projects - Blue Accent */}
      <Card
        className={cn(
          "relative overflow-hidden border-l-[3px] border-l-blue-500 bg-gradient-to-br from-blue-500/10 to-transparent dark:from-blue-500/5 cursor-pointer hover:shadow-lg transition-all",
          activeMetric === 'total' && "ring-2 ring-blue-500"
        )}
        onClick={() => onMetricClick('total')}
        role="button"
        tabIndex={0}
        aria-label="Filter by all projects"
        onKeyDown={keyboardActivate('total')}
      >
        <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
          <CardTitle className="text-sm font-medium">Total Projects</CardTitle>
          <Briefcase className="h-5 w-5 text-info-600 dark:text-info-400" />
        </CardHeader>
        <CardContent>
          <div className="text-3xl font-bold text-info-700 dark:text-info-300">
            {isLoadingStats ? <Loader2 className="h-8 w-8 animate-spin" /> : stats.totalProjects}
          </div>
          <p className="text-xs text-muted-foreground mt-1">
            {stats.activeProjects} active
          </p>
        </CardContent>
      </Card>

      {/* Team Members - Green Accent (Non-clickable for now) */}
      <Card
        className="relative overflow-hidden border-l-[3px] border-l-success-500 bg-gradient-to-br from-success-500/10 to-transparent dark:from-success-500/5"
      >
        <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
          <CardTitle className="text-sm font-medium">Team Members</CardTitle>
          <Users className="h-5 w-5 text-success-600 dark:text-success-400" />
        </CardHeader>
        <CardContent>
          <div className="text-3xl font-bold text-success-700 dark:text-success-300">
            {isLoadingStats ? <Loader2 className="h-8 w-8 animate-spin" /> : stats.totalTeamMembers}
          </div>
          <p className="text-xs text-muted-foreground mt-1">
            Across all projects
          </p>
        </CardContent>
      </Card>

      {/* At Risk - Amber Accent with Pulse Animation */}
      <Card
        className={cn(
          "relative overflow-hidden border-l-[4px] border-l-warning-500 bg-gradient-to-br from-warning-500/20 to-transparent dark:from-warning-500/10 cursor-pointer hover:shadow-lg transition-all animate-pulse-subtle",
          activeMetric === 'at-risk' && "ring-2 ring-warning-500"
        )}
        onClick={() => onMetricClick('at-risk')}
        role="button"
        tabIndex={0}
        aria-label="Filter by at-risk projects"
        onKeyDown={keyboardActivate('at-risk')}
      >
        <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
          <CardTitle className="text-sm font-medium flex items-center gap-2">
            At Risk
            {stats.atRiskCount > 0 && (
              <Badge variant="destructive" className="text-[10px] px-1.5 py-0 h-4">
                ACTION REQUIRED
              </Badge>
            )}
          </CardTitle>
          <AlertCircle className="h-5 w-5 text-warning-600 dark:text-warning-400" />
        </CardHeader>
        <CardContent>
          <div className="text-3xl font-bold text-warning-700 dark:text-warning-300">
            {isLoadingStats ? <Loader2 className="h-8 w-8 animate-spin" /> : stats.atRiskCount}
          </div>
          <p className="text-xs text-muted-foreground mt-1">
            Require immediate attention
          </p>
        </CardContent>
      </Card>

      {/* High Priority - Red Accent */}
      <Card
        className={cn(
          "relative overflow-hidden border-l-[4px] border-l-error-500 bg-gradient-to-br from-error-500/20 to-transparent dark:from-error-500/10 cursor-pointer hover:shadow-lg transition-all",
          activeMetric === 'high-priority' && "ring-2 ring-error-500"
        )}
        onClick={() => onMetricClick('high-priority')}
        role="button"
        tabIndex={0}
        aria-label="Filter by high priority projects"
        onKeyDown={keyboardActivate('high-priority')}
      >
        <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
          <CardTitle className="text-sm font-medium">High Priority</CardTitle>
          <TrendingUp className="h-5 w-5 text-error-600 dark:text-error-400" />
        </CardHeader>
        <CardContent>
          <div className="text-3xl font-bold text-error-700 dark:text-error-300">
            {isLoadingStats ? <Loader2 className="h-8 w-8 animate-spin" /> : stats.highPriorityCount}
          </div>
          <p className="text-xs text-muted-foreground mt-1">
            {stats.totalProjects > 0 ? Math.round((stats.highPriorityCount / stats.totalProjects) * 100) : 0}% of portfolio
          </p>
        </CardContent>
      </Card>
    </div>
  );
};
